import React from 'react';
import { connect } from 'react-redux';
import {
  Box, Typography, Button, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';

import { getPartnerGuid, hasAuthourity } from '../../lib/access.es6';
import {
  getRiskConfigsRequest,
  getRiskConfigRawRequest,
  getRiskConfigRawReset,
  publishRiskConfigRequest,
  publishRiskConfigReset,
  createRiskConfigRequest,
  createRiskConfigReset,
} from './actions.es6';
import RiskConfigEditor from './RiskConfigEditor';

var TH = {
  fontSize: 11, fontWeight: 700, color: '#6B7280',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
  py: 1.25, px: 2,
};
var TD = {
  fontSize: 13, color: '#374151',
  borderBottom: '1px solid #F3F4F6',
  py: 1.5, px: 2,
};

class RiskConfigs extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      editorOpen: false,
      isNew: false,
      selectedConfig: null,
    };
  }

  componentWillMount() {
    this.props.dispatch(getRiskConfigsRequest(getPartnerGuid()));
  }

  componentDidUpdate(prevProps) {
    var successfully_published = this.props.successfully_published;
    var successfully_created = this.props.successfully_created;
    if (
      successfully_published &&
      successfully_published !== prevProps.successfully_published
    ) {
      this.closeEditor();
    }
    if (
      successfully_created &&
      successfully_created !== prevProps.successfully_created
    ) {
      this.closeEditor();
    }
  }

  openEdit(config) {
    this.props.dispatch(getRiskConfigRawReset());
    this.props.dispatch(publishRiskConfigReset());
    this.setState({ editorOpen: true, isNew: false, selectedConfig: config });
    this.props.dispatch(getRiskConfigRawRequest(config.partner));
  }

  openCreate() {
    this.props.dispatch(getRiskConfigRawReset());
    this.props.dispatch(createRiskConfigReset());
    this.setState({ editorOpen: true, isNew: true, selectedConfig: null });
  }

  closeEditor() {
    this.setState({ editorOpen: false, selectedConfig: null });
    this.props.dispatch(getRiskConfigRawReset());
    this.props.dispatch(publishRiskConfigReset());
    this.props.dispatch(createRiskConfigReset());
  }

  handleSave(rawYaml) {
    var isNew = this.state.isNew;
    var selectedConfig = this.state.selectedConfig;
    var partner = getPartnerGuid();
    if (isNew) {
      this.props.dispatch(createRiskConfigRequest(partner, rawYaml));
    } else {
      this.props.dispatch(
        publishRiskConfigRequest(selectedConfig.partner, rawYaml)
      );
    }
  }

  render() {
    if (!hasAuthourity('VIEW_UNDERWRITING_FLOWS')) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            You do not have permission to view Risk Configs.
          </Typography>
        </Box>
      );
    }

    var loader = this.props.loader;
    var risk_configs = this.props.risk_configs;
    var get_configs_error = this.props.get_configs_error;
    var config_raw_loader = this.props.config_raw_loader;
    var risk_config_raw = this.props.risk_config_raw;
    var publish_loader = this.props.publish_loader;
    var publish_error = this.props.publish_error;
    var create_loader = this.props.create_loader;
    var create_error = this.props.create_error;

    var editorOpen = this.state.editorOpen;
    var isNew = this.state.isNew;

    var saving = publish_loader || create_loader;
    var saveError = publish_error || create_error;

    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', mb: 3,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentOutlinedIcon sx={{ color: '#6B7280' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
              Risk Configurations
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={function() { this.openCreate(); }.bind(this)}
            sx={{
              bgcolor: 'success.main',
              '&:hover': { bgcolor: 'success.dark' },
              textTransform: 'none',
            }}
          >
            New Config
          </Button>
        </Box>

        {get_configs_error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load risk configurations. Please try again.
          </Alert>
        )}

        {loader ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ border: '1px solid #E5E7EB', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={TH}>Partner</TableCell>
                  <TableCell sx={TH}>Status</TableCell>
                  <TableCell sx={TH} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!risk_configs || risk_configs.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      sx={{ ...TD, textAlign: 'center', color: '#9CA3AF', py: 4 }}
                    >
                      No risk configurations found for this partner.
                    </TableCell>
                  </TableRow>
                ) : (
                  risk_configs.map(function(config) {
                    return (
                      <TableRow key={config.partner} hover>
                        <TableCell sx={{ ...TD, fontFamily: 'monospace', fontSize: 12 }}>
                          {config.partner}
                        </TableCell>
                        <TableCell sx={TD}>
                          <Chip
                            label={config.enabled !== false ? 'enabled' : 'disabled'}
                            size="small"
                            color={config.enabled !== false ? 'success' : 'default'}
                            sx={{ fontSize: 11 }}
                          />
                        </TableCell>
                        <TableCell sx={TD} align="right">
                          <Tooltip title="Edit configuration">
                            <IconButton
                              size="small"
                              onClick={function() { this.openEdit(config); }.bind(this)}
                              sx={{ color: '#0D47A1' }}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  }.bind(this))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <RiskConfigEditor
          open={editorOpen}
          isNew={isNew}
          partner={getPartnerGuid()}
          rawYaml={risk_config_raw}
          loading={config_raw_loader}
          saving={saving}
          saveError={saveError}
          onSave={this.handleSave.bind(this)}
          onClose={this.closeEditor.bind(this)}
        />
      </Box>
    );
  }
}

function mapStateToProps(state) {
  return {
    loader: state.riskConfigs.loader,
    risk_configs: state.riskConfigs.risk_configs,
    get_configs_error: state.riskConfigs.get_configs_error,

    config_raw_loader: state.riskConfigs.config_raw_loader,
    risk_config_raw: state.riskConfigs.risk_config_raw,
    get_config_raw_error: state.riskConfigs.get_config_raw_error,

    publish_loader: state.riskConfigs.publish_loader,
    successfully_published: state.riskConfigs.successfully_published,
    publish_error: state.riskConfigs.publish_error,

    create_loader: state.riskConfigs.create_loader,
    successfully_created: state.riskConfigs.successfully_created,
    create_error: state.riskConfigs.create_error,
  };
}

export default connect(mapStateToProps)(RiskConfigs);
